import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { TextField } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import { MainService } from 'api/schema'
import type { RobotRead } from 'api/schema'
import { EventStatistics, OdysseyEventStatistics } from 'api/schema/models/EventStatistics'
import clsx from 'clsx'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import { Button as CustomButton } from 'components/Button/Button'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import Loader from 'components/Loader/Loader'
import AdaptiveTabs from 'components/AdaptiveTabs/AdaptiveTabs'
import { StyledTab } from 'components/StyledTab/StyledTab'
import Title from 'components/Title/Title'
import { useTheme } from 'context/ThemeContext'
import { eachDayOfInterval, endOfDay, format, parseISO, startOfDay, subDays, subWeeks } from 'date-fns'
import { FACILITY_NONE_ID } from 'features/tasksNormalized/filterableRecord'
import {
  GAUSIUM_GAP_DEBOUNCE_MS,
  GAUSIUM_GAP_FIRST_POLL_MS,
  GAUSIUM_GAP_MAX_TICKS,
  GAUSIUM_GAP_POLL_MS,
  buildDeltaCursorFromRows,
  runGausiumGapRefreshFollowup,
} from 'features/tasksNormalized/gausiumGapRefreshFollowup'
import { resolveRobotIdsFromFilters } from 'features/tasksNormalized/resolveRobotIdsFromFilters'
import { BubbleChat } from 'flowise-embed-react'
import { useIsMobile } from 'hooks/useIsMobile'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import { useUser } from 'hooks/user'
import autonomicsIcon from 'images/icons/autonomics.png'
import { exportScheduleToCSV } from 'pages/TaskScheduler/TaskSchedulerExport'
import { ProcessedEvent } from 'pages/TaskScheduler/TaskSchedulerUtils'
import { addEventsStatistics } from 'pages/TaskScheduler/TaskSchedulerUtils'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FAVORITES_DROPDOWN_VALUE, narrowToFavoriteIds } from 'utils/favoritesSort'
import { useTranslation } from 'react-i18next'
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { GAUSIUM_ENABLED, SHOW_AI_AGENT } from 'utils/constants'
import type { IRobotInfo } from 'utils/interfaces'
import { enumParam, flagParam, optionalStringParam, stringParam } from 'utils/urlQueryState'
import { parseDashboardTab, serializeDashboardTab } from 'utils/urlViewParams'
import type { VendorCode } from 'utils/vendorDisplayName'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './Dashboard.module.css'
import nonBlockingSyncLoaderStyle from 'styles/nonBlockingSyncLoader.module.css'

interface ChartData {
  fullDate: string
  date: string
  area: number
  time: number
  overallTime: number
  productivity: number
  eventsCount: number
}

type NormalizedItem = {
  vendor_origin_record_type: 'event_statistics' | 'odyssey_event_statistics' | 'gausium_task_report'
  event_id: string
  robot_sn: string
  name: string
  start_time: string
  created_at: string
  updated_at?: string
  end_time?: string | null
  cleaned_area_actual: number
  cleaning_performance: number
  duration_actual: number
  battery_begin: number
  battery_end: number
  battery_capacity_wh?: number
  water_level_begin?: number
  water_level_end?: number
  water_consumption?: number
  water_consumption_unit?: string
  dirty_water_level_begin?: number
  dirty_water_level_end?: number
  dirty_water_consumption?: number
  dirty_water_consumption_unit?: string
  travel_distance_planned?: number
  travel_distance_actual?: number
  cleaned_area_planned?: number
  completion_percentage?: number
  vendor?: VendorCode
  /** Upstream record id: Odyssey.id, or GS task report UUID */
  vendor_origin_record_id?: number | string
  robot_id?: number | null
  scenario_or_map_id?: number | string
  mission_or_area_id?: number | string
  duration_planned?: number | null
  duration_overall?: number | null
  missions_info?: Array<{ id?: number | string; name?: string }>
  missions_progress?: Array<Record<string, unknown>>
}

const FACILITY_NONE_LABEL = 'No facility'

function coerceDashboardId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const n = Number(value.trim())
    return n > 0 ? n : null
  }
  return null
}

/** Mission ids from robot progress. Skip the Odyssey id stored on an overall-only progress row. */
function reportedMissionIds(item: {
  missions_progress?: Array<{ id?: number | string; name?: string }>
  odyssey_id?: number
  vendor_origin_record_id?: number | string
}): number[] {
  const skip = new Set<number>()
  if (typeof item.odyssey_id === 'number') skip.add(item.odyssey_id)
  const origin = coerceDashboardId(item.vendor_origin_record_id)
  if (origin != null) skip.add(origin)
  const ids: number[] = []
  for (const mission of item.missions_progress ?? []) {
    const id = coerceDashboardId(mission?.id)
    if (id == null || skip.has(id)) continue
    ids.push(id)
  }
  return ids
}

function normalizedToEventLike(n: NormalizedItem): EventStatistics & { vendor?: VendorCode; map?: string } {
  const missionId = typeof n.mission_or_area_id === 'number' ? n.mission_or_area_id : 0
  const mapVal = typeof n.scenario_or_map_id === 'string' ? n.scenario_or_map_id : undefined
  const origin = n.vendor_origin_record_id
  const displayAt = n.vendor === VENDOR.GAUSIUM && n.end_time ? n.end_time : n.start_time || n.created_at
  return {
    id: 0,
    event_id: n.event_id,
    task_id: n.vendor_origin_record_type === 'event_statistics' && typeof origin === 'number' ? origin : (0 as any),
    robot_sn: n.robot_sn as any,
    status: 0,
    mission_id: missionId,
    mission_status: 0,
    mission_progress: {
      id: 0,
      name: n.name,
      cleaned_area_actual: n.cleaned_area_actual,
      duration_actual: n.duration_actual,
      cleaning_performance: n.cleaning_performance,
      duration_overall: n.duration_overall ?? n.duration_actual,
      duration_planned: n.duration_planned ?? 0,
      travel_distance_planned: n.travel_distance_planned ?? 0,
      travel_distance_actual: n.travel_distance_actual ?? 0,
      cleaned_area_planned: n.cleaned_area_planned ?? 0,
      water_level_begin: n.water_level_begin ?? 0,
      water_level_end: n.water_level_end ?? 0,
      water_consumption: n.water_consumption,
      water_consumption_unit: n.water_consumption_unit,
      dirty_water_level_begin: n.dirty_water_level_begin ?? 0,
      dirty_water_level_end: n.dirty_water_level_end ?? 0,
      dirty_water_consumption: n.dirty_water_consumption,
      dirty_water_consumption_unit: n.dirty_water_consumption_unit,
      garbage_filled_begin: 0,
      garbage_filled_end: 0,
      battery_begin: n.battery_begin,
      battery_end: n.battery_end,
      battery_capacity_wh: n.battery_capacity_wh,
      missions: [],
      ...(n.completion_percentage !== undefined && { completion_percentage: n.completion_percentage }),
    },
    created_at: displayAt,
    updated_at: displayAt,
    ...(n.vendor === VENDOR.GAUSIUM && { vendor: VENDOR.GAUSIUM as VendorCode, map: mapVal }),
    ...(typeof n.scenario_or_map_id === 'number' && Number.isFinite(n.scenario_or_map_id)
      ? { scenario_id: n.scenario_or_map_id as number }
      : {}),
  } as EventStatistics & { vendor?: VendorCode; map?: string }
}

function normalizedToOdysseyLike(n: NormalizedItem): OdysseyEventStatistics {
  const progress = n.missions_progress?.[0] as Record<string, unknown> | undefined
  const overall = {
    id: 0,
    name: n.name,
    cleaned_area_actual: n.cleaned_area_actual,
    duration_actual: n.duration_actual,
    cleaning_performance: n.cleaning_performance,
    duration_overall: n.duration_overall ?? n.duration_actual,
    duration_planned: n.duration_planned ?? 0,
    travel_distance_planned: n.travel_distance_planned ?? 0,
    travel_distance_actual: n.travel_distance_actual ?? 0,
    cleaned_area_planned: n.cleaned_area_planned ?? 0,
    water_level_begin: n.water_level_begin ?? 0,
    water_level_end: n.water_level_end ?? 0,
    water_consumption: n.water_consumption,
    water_consumption_unit: n.water_consumption_unit,
    dirty_water_level_begin: n.dirty_water_level_begin ?? 0,
    dirty_water_level_end: n.dirty_water_level_end ?? 0,
    dirty_water_consumption: n.dirty_water_consumption,
    dirty_water_consumption_unit: n.dirty_water_consumption_unit,
    garbage_filled_begin: 0,
    garbage_filled_end: 0,
    battery_begin: n.battery_begin,
    battery_end: n.battery_end,
    battery_capacity_wh: n.battery_capacity_wh,
    missions: [],
    ...(n.completion_percentage !== undefined && { completion_percentage: n.completion_percentage }),
  }
  const origin = n.vendor_origin_record_id
  return {
    id: 0,
    event_id: n.event_id,
    odyssey_id: typeof origin === 'number' ? origin : (0 as any),
    robot_sn: n.robot_sn as any,
    status: 0,
    overall_progress: overall,
    missions_info: (n.missions_info ?? []).map(m => ({ id: typeof m.id === 'number' ? m.id : 0, name: m.name ?? '' })),
    missions_progress: (n.missions_progress ?? [overall]) as unknown as OdysseyEventStatistics['missions_progress'],
    created_at: n.start_time || n.created_at,
    updated_at: n.end_time || n.start_time || n.created_at,
    ...(typeof n.scenario_or_map_id === 'number' && Number.isFinite(n.scenario_or_map_id)
      ? { scenario_id: n.scenario_or_map_id as number }
      : {}),
  } as unknown as OdysseyEventStatistics
}

const DATE_PICKER_SLOT_PROPS = {
  popper: {
    sx: {
      '& .MuiPaper-root': {
        background: 'var(--glass-bg) !important',
        backdropFilter: 'var(--glass-blur) !important',
        WebkitBackdropFilter: 'var(--glass-blur) !important',
        borderRadius: 'var(--radius-lg) !important',
        boxShadow: 'var(--glass-shadow) !important',
        border: 'var(--glass-border) !important',
        overflow: 'hidden !important',
      },
    },
  },
  layout: {
    sx: {
      '& .MuiPaper-root, & .MuiPickersLayout-root': {
        background: 'transparent !important',
        color: 'var(--text-main) !important',
        borderRadius: 'var(--radius-lg) !important',
        fontFamily: 'var(--font-main)',
      },
      '& .MuiTypography-root, & .MuiPickersDay-root, & .MuiButtonBase-root': {
        fontFamily: 'var(--font-main)',
        color: 'var(--text-main) !important',
      },
      '& .MuiPickersDay-today': {
        border: '1px solid var(--color-info) !important',
      },
      '& .MuiPickersDay-root.Mui-selected': {
        backgroundColor: 'var(--color-info) !important',
        color: 'white !important',
        borderRadius: 'var(--radius-lg) !important',
      },
      '& .MuiPickersDay-root:hover': {
        backgroundColor: 'var(--glass-bg-hover) !important',
      },
    },
  },
  textField: {
    size: 'small' as const,
    sx: {
      '& .MuiInputBase-root': {
        height: 36,
        width: 160,
        borderRadius: 'var(--radius-lg)',
        backgroundColor: 'var(--glass-bg)',
        backdropFilter: 'var(--glass-blur)',
        boxShadow: 'var(--glass-shadow)',
        color: 'var(--text-main)',
        fontSize: '14px',
        fontFamily: 'inherit',
        '& fieldset': {
          border: 'var(--glass-border) !important',
        },
      },
      '& .MuiInputBase-input': {
        padding: '0 12px',
        height: 36,
        boxSizing: 'border-box',
        fontFamily: 'inherit',
      },
      '& .MuiSvgIcon-root': {
        color: 'var(--text-secondary)',
        fontSize: '1.2rem',
      },
    },
  },
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length > 0) {
    const visiblePayload = payload.filter((e: any) => e.name !== 'Avg. Actual Area (m²)')
    const data = payload[0].payload
    const count = data.eventsCount
    const completed = data.completedCount
    const canceled = data.canceledCount

    // Format hours for the time-based metrics
    const formatValue = (value: any, name: string) => {
      if ((name === 'Working Time (h)' || name === 'Overall Time (h)') && typeof value === 'number') {
        const totalMinutes = Math.floor(value * 60)
        const hours = Math.floor(totalMinutes / 60)
        const minutes = totalMinutes % 60
        return `${hours}h ${minutes}m`
      }
      return value
    }

    return (
      <div
        style={{
          backgroundColor: 'var(--solid-bg)',
          borderRadius: 'var(--radius-sm)',
          border: 'var(--glass-border)',
          boxShadow: 'var(--glass-shadow)',
          fontSize: '12px',
          padding: '10px',
        }}
      >
        <div
          style={{
            color: 'var(--text-main)',
            marginBottom: '8px',
            fontWeight: 'bold',
            borderBottom: '1px solid var(--glass-border)',
            paddingBottom: '4px',
          }}
        >
          {label} ({count} {count === 1 ? 'task' : 'tasks'})
        </div>
        {visiblePayload.map((entry: any, index: number) => (
          <div
            key={index}
            style={{
              color: entry.color,
              padding: '2px 0',
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
            }}
          >
            <span>{entry.name}:</span>
            <span style={{ fontWeight: 'bold' }}>{formatValue(entry.value, entry.name)}</span>
          </div>
        ))}
      </div>
    )
  }
  return null
}

const renderChartLegend = (props: any) => {
  const { payload } = props
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', paddingTop: '20px', gap: '12px' }}>
      {payload.map((entry: any, index: number) => {
        const isDashed = !!entry.payload?.strokeDasharray
        const isBar = entry.type === 'rect' || entry.type === 'square'
        return (
          <div
            key={index}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 14, color: 'var(--text-main)' }}
          >
            <svg width="28" height="12">
              {isDashed ? (
                <line x1="0" y1="6" x2="26" y2="6" stroke={entry.color} strokeWidth="2" strokeDasharray="5 4" />
              ) : isBar ? (
                <rect x="2" y="2" width="24" height="8" fill={entry.color} rx="2" />
              ) : (
                <>
                  <line x1="0" y1="6" x2="26" y2="6" stroke={entry.color} strokeWidth="2" />
                  <circle cx="14" cy="6" r="3" fill={entry.color} />
                </>
              )}
            </svg>
            <span>{entry.value}</span>
          </div>
        )
      })}
    </div>
  )
}

/** useNormalizedApi: two requests to normalized API (source=autonomics, source=gausium_task_reports); UI unblocks when autonomics is back. Default: legacy three endpoints. */
const Dashboard = ({ useNormalizedApi = false }: { useNormalizedApi?: boolean } = {}) => {
  const { t } = useTranslation()
  const user = useUser()
  const [url, setUrl] = useUrlQueryState({
    tab: {
      parse: parseDashboardTab,
      serialize: serializeDashboardTab,
    },
    range: enumParam(['day', 'week', 'month', 'custom'] as const, 'month'),
    from: optionalStringParam(),
    to: optionalStringParam(),
    robot_sn: stringParam(),
    taskId: stringParam(),
    missionId: stringParam(),
    facility: stringParam(),
    vendor: optionalStringParam(),
    rfav: flagParam(),
  })
  const isMobile = useIsMobile()
  const [showFilters, setShowFilters] = useState(false)
  const [loading, setLoading] = useState(true)
  const activeTab = url.tab
  const setActiveTab = (tab: number) => setUrl({ tab })
  const fetchIdRef = useRef(0)
  const hasAppliedInitialVendorDefaultRef = useRef(false)
  const settledRef = useRef({ event: false, odyssey: false, gausium: false })
  const eventResultsRef = useRef<EventStatistics[]>([])
  const odysseyResultsRef = useRef<OdysseyEventStatistics[]>([])
  const gausiumResultsRef = useRef<any[]>([])

  // Raw Data (separate so UI updates as each source arrives)
  const [eventStatisticsResults, setEventStatisticsResults] = useState<EventStatistics[]>([])
  const [gausiumTaskReportResults, setGausiumTaskReportResults] = useState<EventStatistics[]>([])
  const [isGausiumSyncing, setIsGausiumSyncing] = useState(false)
  const [rawOdysseys, setRawOdysseys] = useState<OdysseyEventStatistics[]>([])
  const [gausiumRobotSns, setGausiumRobotSns] = useState<string[]>([])

  // Merged events for filters/charts
  const rawEvents = useMemo(
    () => [...eventStatisticsResults, ...gausiumTaskReportResults],
    [eventStatisticsResults, gausiumTaskReportResults]
  )

  // Filters
  const filterRobotSn = url.robot_sn
  const filterMissionId = url.missionId
  const setFilterMissionId = (missionId: string) => setUrl({ missionId })
  const filterTaskId = url.taskId
  const setFilterTaskId = (taskId: string) => setUrl({ taskId })
  const filterFacilityId = url.facility
  const setFilterFacilityId = (facility: string) => setUrl({ facility })
  const filterVendor = url.vendor as VendorCode | ''
  const setFilterVendor = (vendor: VendorCode | '') => setUrl({ vendor })
  const favoritesOnly = url.rfav && !url.robot_sn.trim()
  const favoriteRobotIds = useMemo(
    () => user?.state.favoriteRobotIds ?? [],
    [user?.state.favoriteRobotIds]
  )
  const [facilities, setFacilities] = useState<any[]>([])
  /** Same catalog as TaskSchedulerNormalized — for `robot_ids` scope on normalized GET. */
  const [robotsReadList, setRobotsReadList] = useState<RobotRead[]>([])
  const [robotsCatalogLoaded, setRobotsCatalogLoaded] = useState(false)
  const [robotNamesBySn, setRobotNamesBySn] = useState<Record<string, string>>({})
  const [robotVendorsBySn, setRobotVendorsBySn] = useState<Record<string, VendorCode>>({})
  const [robotSnById, setRobotSnById] = useState<Record<number, string>>({})
  const [facilityRobotSns, setFacilityRobotSns] = useState<Set<string>>(new Set())
  const [facilityRobotSnsById, setFacilityRobotSnsById] = useState<Record<string, Set<string>>>({})
  const [facilityRobotSnsByIdReady, setFacilityRobotSnsByIdReady] = useState<boolean>(false)
  const [autoExtendDays, setAutoExtendDays] = useState<number>(0)
  /** Scenario/mission id→display name for filter dropdown (same catalogs as Task Scheduler). */
  const [missionScenarioCatalogNames, setMissionScenarioCatalogNames] = useState<{
    scenarios: Record<number, string>
    missions: Record<number, string>
  }>({ scenarios: {}, missions: {} })

  // Date Filters
  const { theme: currentTheme } = useTheme()
  const isDark = currentTheme === 'dark'

  const dateRangeType = url.range
  const hasActiveFilters = useMemo(() => {
    return !!(
      filterRobotSn ||
      filterMissionId ||
      filterTaskId ||
      filterFacilityId ||
      filterVendor ||
      favoritesOnly ||
      dateRangeType !== 'month'
    )
  }, [filterRobotSn, filterMissionId, filterTaskId, filterFacilityId, filterVendor, favoritesOnly, dateRangeType])
  const customStartDate = url.from || format(subDays(new Date(), 30), 'yyyy-MM-dd')
  const customEndDate = url.to || format(new Date(), 'yyyy-MM-dd')
  const setDateRangeType = (range: 'day' | 'week' | 'month' | 'custom') => {
    if (range === 'custom') {
      // Functional patch: if coalesced with a prior from/to write, keep the newer values.
      setUrl(s => ({
        range,
        from: s.from || format(subDays(new Date(), 30), 'yyyy-MM-dd'),
        to: s.to || format(new Date(), 'yyyy-MM-dd'),
      }))
      return
    }
    setUrl({ range, from: '', to: '' })
  }
  const setCustomStartDate = (from: string) =>
    setUrl(s => ({
      range: 'custom',
      from,
      to: s.to || format(new Date(), 'yyyy-MM-dd'),
    }))
  const setCustomEndDate = (to: string) =>
    setUrl(s => ({
      range: 'custom',
      to,
      from: s.from || format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    }))

  // 1a. Calculate Effective Date Range
  const effectiveDateRange = useMemo(() => {
    let startDate: Date
    let endDate: Date = new Date()

    switch (dateRangeType) {
      case 'day':
        startDate = startOfDay(new Date())
        endDate = endOfDay(new Date())
        break
      case 'week':
        startDate = startOfDay(subWeeks(new Date(), 1))
        break
      case 'month':
        startDate = startOfDay(subDays(new Date(), 30))
        break
      case 'custom':
        // Handle potential invalid dates gracefully
        const parsedStart = parseISO(customStartDate)
        const parsedEnd = parseISO(customEndDate)
        if (!isNaN(parsedStart.getTime()) && !isNaN(parsedEnd.getTime())) {
          startDate = startOfDay(parsedStart)
          endDate = endOfDay(parsedEnd)
        } else {
          // Fallback
          startDate = startOfDay(subDays(new Date(), 30))
        }
        break
      default:
        startDate = startOfDay(subDays(new Date(), 30))
    }
    return { startDate, endDate }
  }, [dateRangeType, customStartDate, customEndDate])

  /** Same rules as TaskSchedulerNormalized — narrow normalized GET + GS gap to filtered fleet. */
  const normalizedRobotScopeIds = useMemo(() => {
    const scoped = resolveRobotIdsFromFilters(
      {
        vendor: filterVendor,
        facilityId: filterFacilityId,
        robotSn: filterRobotSn,
        missionKey: '',
        startAfterHour: -1,
      },
      robotsReadList,
      facilities
    )
    if (!favoritesOnly) return scoped
    return narrowToFavoriteIds(scoped, favoriteRobotIds)
  }, [filterVendor, filterFacilityId, filterRobotSn, robotsReadList, facilities, favoritesOnly, favoriteRobotIds])

  const favoriteSnSet = useMemo(() => {
    const sns = new Set<string>()
    for (const id of favoriteRobotIds) {
      const sn = robotSnById[id]
      if (sn) sns.add(sn)
    }
    return sns
  }, [favoriteRobotIds, robotSnById])

  const needsRobotRosterForNormalizedScope = useMemo(() => {
    const fid = filterFacilityId.trim()
    const hasFacilityScope = Boolean(fid && fid !== FACILITY_NONE_ID)
    return favoritesOnly || Boolean(filterRobotSn.trim()) || Boolean(filterVendor) || hasFacilityScope
  }, [favoritesOnly, filterRobotSn, filterVendor, filterFacilityId])

  useEffect(() => {
    let cancelled = false
    Promise.all([MainApi.fetchScenarios(1, 3000), MainService.mainMissionList(['-id'], 1, 3000)])
      .then(([scenRes, missRes]) => {
        if (cancelled) return
        const scenarios: Record<number, string> = {}
        const missions: Record<number, string> = {}
        scenRes.results?.forEach((s: { id?: number; name?: string }) => {
          if (typeof s.id === 'number' && typeof s.name === 'string') scenarios[s.id] = s.name
        })
        missRes.results?.forEach((m: { id?: number; name?: string | null }) => {
          if (typeof m.id === 'number' && typeof m.name === 'string') missions[m.id] = m.name
        })
        setMissionScenarioCatalogNames({ scenarios, missions })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  // 1b. Fetch Data
  // useNormalizedApi: two requests to normalized API (autonomics = event+odyssey from DB, gausium = GS); UI unblocks when autonomics is back.
  // else: legacy three endpoints in parallel.
  useEffect(() => {
    const { startDate, endDate } = effectiveDateRange
    if (useNormalizedApi && needsRobotRosterForNormalizedScope && !robotsCatalogLoaded) {
      return () => {}
    }
    const fetchId = Date.now()
    fetchIdRef.current = fetchId
    settledRef.current = { event: false, odyssey: false, gausium: false }
    eventResultsRef.current = []
    odysseyResultsRef.current = []
    gausiumResultsRef.current = []
    setLoading(true)
    setEventStatisticsResults([])
    setGausiumTaskReportResults([])
    setRawOdysseys([])
    setGausiumRobotSns([])
    setIsGausiumSyncing(false)

    if (useNormalizedApi) {
      let cancelled = false
      const cleanupNormalized = () => {
        cancelled = true
      }

      const params: { start_date: string; end_date: string; robot_ids?: number[] } = {
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        ...(normalizedRobotScopeIds !== undefined ? { robot_ids: normalizedRobotScopeIds } : {}),
      }

      const maybeDone = () => {
        const s = settledRef.current
        if (!s.event || !s.odyssey) return
        setLoading(false)
      }

      const sn = filterRobotSn?.trim()
      const isActuallyGausium = filterVendor === VENDOR.GAUSIUM
      const isActuallyAutonomics = filterVendor === VENDOR.AUTONOMICS

      if (!isActuallyGausium) {
        MainApi.fetchUnifiedEvents({ ...params, source: 'autonomics' })
          .then(res => {
            if (fetchIdRef.current !== fetchId) return
            const list = (res?.results ?? []) as NormalizedItem[]
            const events: (EventStatistics & { vendor?: VendorCode; map?: string })[] = []
            const odysseys: OdysseyEventStatistics[] = []
            list.forEach(item => {
              if (item.vendor_origin_record_type === 'event_statistics') {
                events.push(normalizedToEventLike(item))
              } else if (item.vendor_origin_record_type === 'odyssey_event_statistics') {
                odysseys.push(normalizedToOdysseyLike(item))
              }
            })
            eventResultsRef.current = events
            odysseyResultsRef.current = odysseys
            setEventStatisticsResults(events)
            setRawOdysseys(odysseys)
            settledRef.current = { ...settledRef.current, event: true, odyssey: true }
            maybeDone()
          })
          .catch(() => {
            if (fetchIdRef.current !== fetchId) return
            settledRef.current = { ...settledRef.current, event: true, odyssey: true }
            maybeDone()
          })
      } else {
        settledRef.current = { ...settledRef.current, event: true, odyssey: true }
        maybeDone()
      }

      if (!isActuallyAutonomics) {
        // Phase 1: instant cached data (no GS API calls).
        MainApi.fetchUnifiedEvents({ ...params, source: 'gausium_task_reports', cache_only: true })
          .then(res => {
            if (fetchIdRef.current !== fetchId) return
            const list = (res?.results ?? []) as NormalizedItem[]
            const gausium: (EventStatistics & { vendor?: VendorCode; map?: string })[] = []
            const gausiumSns = new Set<string>()
            list.forEach(item => {
              gausium.push(normalizedToEventLike(item))
              gausiumSns.add(String(item.robot_sn).trim())
            })
            gausiumResultsRef.current = gausium
            setGausiumTaskReportResults(gausium)
            setGausiumRobotSns(Array.from(gausiumSns))
            settledRef.current = { ...settledRef.current, gausium: true }
            maybeDone()

            if (GAUSIUM_ENABLED) {
              const gsRowsByEventId = new Map<string, NormalizedItem>()
              list.forEach(item => {
                const id = String(item.event_id ?? '')
                if (id) gsRowsByEventId.set(id, item)
              })
              setIsGausiumSyncing(true)
              void runGausiumGapRefreshFollowup({
                debounceMs: GAUSIUM_GAP_DEBOUNCE_MS,
                firstPollDelayMs: GAUSIUM_GAP_FIRST_POLL_MS,
                pollIntervalMs: GAUSIUM_GAP_POLL_MS,
                maxTicks: GAUSIUM_GAP_MAX_TICKS,
                minTicksBeforeStable: 0,
                stableRounds: 0,
                queueRefresh: () => MainApi.queueGausiumNormalizedGapRefresh(params),
                fetchSyncStatus: () => MainApi.fetchGausiumNormalizedSyncStatus(params),
                initialCursor: (res as { snapshot_cursor?: string }).snapshot_cursor ?? buildDeltaCursorFromRows(list),
                fetchDelta: cursor =>
                  MainApi.fetchGausiumNormalizedDelta({
                    ...params,
                    cursor,
                    limit: 1000,
                  }),
                isStale: () => cancelled || fetchIdRef.current !== fetchId,
                onDelta: rows => {
                  if (fetchIdRef.current !== fetchId) return
                  const incoming = rows as NormalizedItem[]
                  incoming.forEach(item => {
                    const id = String(item.event_id ?? '')
                    if (!id) return
                    gsRowsByEventId.set(id, item)
                  })
                  const list2 = Array.from(gsRowsByEventId.values())
                  const gausium2: (EventStatistics & { vendor?: VendorCode; map?: string })[] = []
                  const gausiumSns2 = new Set<string>()
                  list2.forEach(item => {
                    gausium2.push(normalizedToEventLike(item))
                    gausiumSns2.add(String(item.robot_sn).trim())
                  })
                  gausiumResultsRef.current = gausium2
                  setGausiumTaskReportResults(gausium2)
                  setGausiumRobotSns(Array.from(gausiumSns2))
                },
              }).finally(() => {
                if (cancelled || fetchIdRef.current !== fetchId) return
                setIsGausiumSyncing(false)
              })
            }
          })
          .catch(() => {
            if (fetchIdRef.current !== fetchId) return
            settledRef.current = { ...settledRef.current, gausium: true }
            maybeDone()
          })
      } else {
        settledRef.current = { ...settledRef.current, gausium: true }
        maybeDone()
      }
      return cleanupNormalized
    }

    const pageParams = {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      page_size: 1000,
      page: 1,
    }

    const fetchAllPages = async (apiCall: (params: any) => Promise<{ results: any[]; total: number }>) => {
      const firstPage = await apiCall({ ...pageParams, page: 1 })
      const firstResults = firstPage?.results ?? []
      let allResults = [...firstResults]
      const total = firstPage?.total ?? 0
      if (total > 1000) {
        const totalPages = Math.ceil(total / 1000)
        const restPages = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) => apiCall({ ...pageParams, page: i + 2 }))
        )
        restPages.forEach(res => {
          allResults = [...allResults, ...(res.results ?? [])]
        })
      }
      return { results: allResults, total }
    }

    const gausiumParams: { start_date: string; end_date: string; robot_sn?: string } = {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    }
    if (filterRobotSn?.trim()) {
      gausiumParams.robot_sn = filterRobotSn.trim()
    }

    const maybeDone = () => {
      const s = settledRef.current
      // Снимаем общий loading, когда готовы данные из БД (event + odyssey).
      // Gausium может догружаться дольше, но не блокирует отображение дашборда.
      if (!s.event || !s.odyssey) return
      setLoading(false)
    }

    const onFulfill = (source: 'event' | 'odyssey' | 'gausium') => {
      return (data: any) => {
        if (fetchIdRef.current !== fetchId) return
        if (source === 'event') {
          const results = data?.results ?? []
          eventResultsRef.current = results
          setEventStatisticsResults(results)
        } else if (source === 'odyssey') {
          const results = data?.results ?? []
          odysseyResultsRef.current = results
          setRawOdysseys(results)
        } else {
          const results = data?.results ?? []
          const sns = data?.gausium_robot_sns ?? []
          gausiumResultsRef.current = results
          setGausiumTaskReportResults(results)
          setGausiumRobotSns(sns)
        }
        settledRef.current = { ...settledRef.current, [source]: true }
        maybeDone()
      }
    }

    const onReject = (source: 'event' | 'odyssey' | 'gausium') => {
      if (fetchIdRef.current !== fetchId) return
      settledRef.current = { ...settledRef.current, [source]: true }
      maybeDone()
    }

    const sn = filterRobotSn?.trim()
    const isActuallyGausium = filterVendor === VENDOR.GAUSIUM
    const isActuallyAutonomics = filterVendor === VENDOR.AUTONOMICS

    if (!isActuallyGausium) {
      fetchAllPages(MainApi.fetchEventStatistics)
        .then(data => {
          onFulfill('event')(data)
        })
        .catch(() => {
          onReject('event')
        })

      fetchAllPages(MainApi.fetchOdysseyEventStatistics)
        .then(data => {
          onFulfill('odyssey')(data)
        })
        .catch(() => {
          onReject('odyssey')
        })
    } else {
      onReject('event')
      onReject('odyssey')
    }

    if (!isActuallyAutonomics) {
      MainApi.fetchGausiumTaskReports(gausiumParams)
        .then(data => {
          onFulfill('gausium')(data)
        })
        .catch(() => {
          onReject('gausium')
        })
    } else {
      onReject('gausium')
    }
  }, [
    dateRangeType,
    customStartDate,
    customEndDate,
    filterRobotSn,
    filterVendor,
    filterFacilityId,
    useNormalizedApi,
    robotsReadList,
    normalizedRobotScopeIds,
    needsRobotRosterForNormalizedScope,
    robotsCatalogLoaded,
  ])

  // AI Agent Embed
  useEffect(() => {
    if (!SHOW_AI_AGENT) return

    const script = document.createElement('script')
    script.src = 'https://ai.autonomics.tech/api/embed/bundle.js'
    script.defer = true
    document.body.appendChild(script)

    return () => {
      document.body.removeChild(script)
    }
  }, [])

  // 1c. Fetch Facilities & Selected Facility Robots
  useEffect(() => {
    MainApi.fetchFacilities(1, 1000).then(res => setFacilities(res.results))
  }, [])

  useEffect(() => {
    RobotApi.fetchRobots(1, 1000)
      .then((res: { results: IRobotInfo[] }) => {
        const mapping: Record<string, string> = {}
        const vendorMapping: Record<string, VendorCode> = {}
        const idMapping: Record<number, string> = {}
        const reads: RobotRead[] = []
        ;(res?.results ?? []).forEach(robot => {
          const id = typeof robot?.id === 'number' ? robot.id : null
          const sn = String(robot?.serialNumber ?? '').trim()
          const name = String(robot?.name ?? '').trim()
          if (sn && name) mapping[sn] = name
          if (sn && robot?.vendor) vendorMapping[sn] = robot.vendor
          if (id != null && sn) idMapping[id] = sn
          if (robot?.robotRaw) reads.push(robot.robotRaw)
        })
        setRobotNamesBySn(mapping)
        setRobotVendorsBySn(vendorMapping)
        setRobotSnById(idMapping)
        setRobotsReadList(reads)
        setRobotsCatalogLoaded(true)
      })
      .catch(() => {
        setRobotNamesBySn({})
        setRobotVendorsBySn({})
        setRobotSnById({})
        setRobotsReadList([])
        setRobotsCatalogLoaded(true)
      })
  }, [])

  useEffect(() => {
    if (hasAppliedInitialVendorDefaultRef.current || !robotsCatalogLoaded) return
    hasAppliedInitialVendorDefaultRef.current = true
    const hasAutonomics = Object.values(robotVendorsBySn).some(vendor => vendor === VENDOR.AUTONOMICS)
    if (hasAutonomics) {
      setFilterVendor(filterVendor || VENDOR.AUTONOMICS)
    }
  }, [robotsCatalogLoaded, robotVendorsBySn, filterVendor])

  // When opening dashboard for a specific GS robot (from assets/robots list),
  // do not force default vendor=Autonomics, otherwise GS stats are filtered out.
  useEffect(() => {
    const sn = filterRobotSn?.trim()
    if (!sn) return
    const vendor = robotVendorsBySn[sn]
    if (vendor === VENDOR.GAUSIUM && filterVendor === VENDOR.AUTONOMICS) {
      setFilterVendor('')
    }
  }, [filterRobotSn, robotVendorsBySn, filterVendor])

  // Build facility -> robot SNs mapping so we can filter facilities dynamically.
  useEffect(() => {
    if (!facilities?.length) {
      setFacilityRobotSnsById({})
      setFacilityRobotSnsByIdReady(true)
      return
    }
    // If we don't have robots mapping yet, wait.
    if (!robotSnById || Object.keys(robotSnById).length === 0) return

    const next: Record<string, Set<string>> = {}
    facilities.forEach((f: any) => {
      const facId = String(f.id)
      const ids: number[] = f.robots_ids || []
      const sns = new Set<string>()
      ids.forEach(id => {
        const sn = robotSnById[id]
        if (sn) sns.add(String(sn).trim())
      })
      next[facId] = sns
    })
    setFacilityRobotSnsById(next)
    setFacilityRobotSnsByIdReady(true)
  }, [facilities, robotSnById])

  useEffect(() => {
    if (filterFacilityId) {
      if (filterFacilityId === FACILITY_NONE_ID) {
        // If mapping isn't ready yet, avoid showing misleading results.
        if (!facilityRobotSnsByIdReady) {
          setFacilityRobotSns(new Set())
          return
        }
        const allInAnyFacility = new Set<string>()
        ;(Object.values(facilityRobotSnsById) as Set<string>[]).forEach((sns: Set<string>) => {
          sns.forEach((sn: string) => allInAnyFacility.add(sn))
        })
        const allKnownRobotSns = new Set<string>()
        ;[...rawEvents, ...rawOdysseys].forEach((item: any) => {
          const sn = String(item?.robot_sn ?? '').trim()
          if (sn) allKnownRobotSns.add(sn)
        })
        const withoutFacility = new Set<string>()
        allKnownRobotSns.forEach(sn => {
          if (!allInAnyFacility.has(sn)) withoutFacility.add(sn)
        })
        setFacilityRobotSns(withoutFacility)
        return
      }

      // Use preloaded facility -> SN mapping (no extra requests).
      if (!facilityRobotSnsByIdReady) {
        setFacilityRobotSns(new Set())
        return
      }
      const sns = facilityRobotSnsById[String(filterFacilityId)] || new Set<string>()
      setFacilityRobotSns(new Set(Array.from(sns)))
    } else {
      setFacilityRobotSns(new Set())
    }
  }, [filterFacilityId, facilityRobotSnsById, facilityRobotSnsByIdReady, rawEvents, rawOdysseys])
  // 2. Filter Logic (Client-Side)
  const filteredData = useMemo(() => {
    const filterSn = filterRobotSn ? String(filterRobotSn).trim() : null
    const getVendor = (item: any): VendorCode => (item.vendor === VENDOR.GAUSIUM ? VENDOR.GAUSIUM : VENDOR.AUTONOMICS)
    const filterMissionNum = filterMissionId && !filterMissionId.startsWith('map:') ? Number(filterMissionId) : null
    const hasFilterMissionNum = filterMissionNum != null && Number.isFinite(filterMissionNum)

    const events = rawEvents.filter((e: any) => {
      // Date Range Filter
      const eventTime = parseISO(e.created_at).getTime()
      if (eventTime < effectiveDateRange.startDate.getTime() || eventTime > effectiveDateRange.endDate.getTime()) {
        return false
      }

      if (filterVendor && getVendor(e) !== filterVendor) return false
      // Facility
      if (filterFacilityId && !facilityRobotSns.has(String(e.robot_sn).trim())) return false
      // Robot SN
      if (favoritesOnly && robotsCatalogLoaded && !favoriteSnSet.has(String(e.robot_sn).trim())) return false
      if (filterSn && String(e.robot_sn).trim() !== filterSn) return false
      // Mission: Autonomics = mission_id, Gausium = map (id "map:...")
      if (filterMissionId) {
        if (filterMissionId.startsWith('map:')) {
          if (e.vendor !== VENDOR.GAUSIUM || e.map !== filterMissionId.slice(4)) return false
        } else if (
          !(
            (hasFilterMissionNum && e.mission_id === filterMissionNum) ||
            (hasFilterMissionNum && e.scenario_id === filterMissionNum)
          )
        )
          return false
      }
      // Task: Autonomics = task_id, Gausium = mission_progress.name (id "task:...")
      if (filterTaskId) {
        if (filterTaskId.startsWith('task:')) {
          if (e.mission_progress?.name !== filterTaskId.slice(5)) return false
        } else if (e.task_id !== Number(filterTaskId)) return false
      }
      return true
    })

    const odysseys = rawOdysseys.filter((o: any) => {
      // Date Range Filter
      const eventTime = parseISO(o.created_at).getTime()
      if (eventTime < effectiveDateRange.startDate.getTime() || eventTime > effectiveDateRange.endDate.getTime()) {
        return false
      }

      if (filterVendor && getVendor(o) !== filterVendor) return false
      // Facility
      if (filterFacilityId && !facilityRobotSns.has(String(o.robot_sn).trim())) return false
      // Robot SN
      if (favoritesOnly && robotsCatalogLoaded && !favoriteSnSet.has(String(o.robot_sn).trim())) return false
      if (filterSn && String(o.robot_sn).trim() !== filterSn) return false
      // Map (Gausium) filter doesn't apply to Odysseys: without this, selecting map:Asbis still includes Odyssey stats
      // (e.g. robots 1000/12345), inflating "tasks" count for the selected map.
      if (filterMissionId && filterMissionId.startsWith('map:')) return false
      if (
        filterMissionId &&
        !(
          (hasFilterMissionNum && o.scenario_id === filterMissionNum) ||
          (o.missions_info || []).some((m: any) => m.id === filterMissionNum) ||
          (hasFilterMissionNum && reportedMissionIds(o).includes(filterMissionNum))
        )
      )
        return false
      if (filterTaskId && !filterTaskId.startsWith('task:') && o.odyssey_id !== Number(filterTaskId)) return false
      return true
    })

    return { events, odysseys }
  }, [
    rawEvents,
    rawOdysseys,
    filterRobotSn,
    filterMissionId,
    filterTaskId,
    filterFacilityId,
    filterVendor,
    favoritesOnly,
    favoriteSnSet,
    robotsCatalogLoaded,
    facilityRobotSns,
    effectiveDateRange,
  ])

  // 3. Aggregated Stats
  const stats = useMemo(() => {
    let totalArea = 0
    let totalTime = 0
    let totalOverallTime = 0

    // Process Events
    filteredData.events.forEach(e => {
      const area = e.mission_progress?.cleaned_area_actual || 0
      if (area <= 0) return
      const time = e.mission_progress?.duration_actual || 0
      const overallTime = e.mission_progress?.duration_overall || e.mission_progress?.duration_actual || 0
      totalArea += area
      totalTime += time
      totalOverallTime += overallTime
    })

    // Process Odysseys
    filteredData.odysseys.forEach(o => {
      const area = o.overall_progress?.cleaned_area_actual || 0
      if (area <= 0) return
      const time = o.overall_progress?.duration_actual || 0
      const overallTime = o.overall_progress?.duration_overall || o.overall_progress?.duration_actual || 0
      totalArea += area
      totalTime += time
      totalOverallTime += overallTime
    })

    // Simple average of all individual event performance values
    let sumPerformance = 0
    let countPerformance = 0

    filteredData.events.forEach(e => {
      const area = e.mission_progress?.cleaned_area_actual || 0
      if (area <= 0) return
      const p = e.mission_progress?.cleaning_performance
      if (typeof p === 'number' && p > 0) {
        sumPerformance += p
        countPerformance++
      }
    })

    filteredData.odysseys.forEach(o => {
      const area = o.overall_progress?.cleaned_area_actual || 0
      if (area <= 0) return
      const p = o.overall_progress?.cleaning_performance
      if (typeof p === 'number' && p > 0) {
        sumPerformance += p
        countPerformance++
      }
    })

    const avgPerformance = countPerformance > 0 ? sumPerformance / countPerformance : 0

    // Average completion percentage
    let sumCompletion = 0
    let countCompletion = 0

    const calcCompletion = (progress: any) => {
      if (!progress) return null
      const completionRaw = progress.completion_percentage
      if (completionRaw != null && Number.isFinite(Number(completionRaw))) {
        let pct = Number(completionRaw)
        if (pct >= 0 && pct <= 1) pct = pct * 100
        return Math.max(0, Math.min(100, pct))
      }
      if (progress.travel_distance_planned > 0) {
        let pct = (progress.travel_distance_actual / progress.travel_distance_planned) * 100
        return Math.max(0, Math.min(100, pct))
      }
      if (progress.cleaned_area_planned > 0) {
        let pct = (progress.cleaned_area_actual / progress.cleaned_area_planned) * 100
        return Math.max(0, Math.min(100, pct))
      }
      return null
    }

    const getWaterLiters = (progress: any) => {
      if (!progress) return null
      if (progress.water_consumption != null && progress.water_consumption > 0) {
        const val = Number(progress.water_consumption)
        const unit = progress.water_consumption_unit || 'percent'
        if (unit === 'liters' && val <= 1000) return val
        if (unit === 'percent' && val <= 100) return (val / 100) * 75
        return null
      }
      const begin = progress.water_level_begin || 0
      const end = progress.water_level_end || 0
      if (begin > 100 || end > 100 || end > begin) return null
      return ((begin - end) / 100) * 75
    }

    const getEleckWh = (progress: any) => {
      if (!progress) return null
      const begin = progress.battery_begin || 0
      const end = progress.battery_end || 0
      if (begin > 100 || end > 100 || end > begin) return null
      const capacityKWh = (progress.battery_capacity_wh ?? 11000) / 1000
      return ((begin - end) / 100) * capacityKWh
    }

    let totalWaterL = 0
    let totalEleckWh = 0
    let totalAreaForWater = 0
    let totalAreaForElec = 0

    filteredData.events.forEach(e => {
      const area = e.mission_progress?.cleaned_area_actual || 0
      if (area <= 0) return
      const c = calcCompletion(e.mission_progress)
      if (c !== null) {
        sumCompletion += c
        countCompletion++
      }

      const waterL = getWaterLiters(e.mission_progress)
      const eleckWh = getEleckWh(e.mission_progress)
      if (waterL !== null && eleckWh !== null) {
        totalWaterL += waterL
        totalAreaForWater += area
        totalEleckWh += eleckWh
        totalAreaForElec += area
      }
    })

    filteredData.odysseys.forEach(o => {
      const area = o.overall_progress?.cleaned_area_actual || 0
      if (area <= 0) return
      const c = calcCompletion(o.overall_progress)
      if (c !== null) {
        sumCompletion += c
        countCompletion++
      }

      const waterL = getWaterLiters(o.overall_progress)
      const eleckWh = getEleckWh(o.overall_progress)
      if (waterL !== null && eleckWh !== null) {
        totalWaterL += waterL
        totalAreaForWater += area
        totalEleckWh += eleckWh
        totalAreaForElec += area
      }
    })

    const avgCompletion = countCompletion > 0 ? sumCompletion / countCompletion : 0
    const avgWaterPerM2 = totalAreaForWater > 0 ? totalWaterL / totalAreaForWater : 0
    const avgElecWhPerM2 = totalAreaForElec > 0 ? (totalEleckWh * 1000) / totalAreaForElec : 0

    return {
      totalArea,
      totalTime,
      totalOverallTime,
      avgPerformance,
      avgCompletion,
      avgWaterPerM2,
      avgElecWhPerM2,
      totalWaterL,
      totalEleckWh,
    }
  }, [filteredData, effectiveDateRange])

  // 4. Chart Data
  const chartData = useMemo(() => {
    const aggregatedData: Record<
      string,
      {
        area: number
        sumAreaPlanned: number
        time: number
        overallTime: number
        sumProductivity: number
        prodCount: number
        sumCompletion: number
        completionCount: number
        eventsCount: number
        completedCount: number
        canceledCount: number
        sumWaterL: number
        sumAreaForWater: number
        sumEleckWh: number
        sumAreaForElec: number
      }
    > = {}

    const getStatus = (item: any) => {
      if (item.mission_status !== undefined) return item.mission_status
      if (item.status !== undefined) return item.status
      return 0
    }

    const calcCompletion = (progress: any) => {
      if (!progress) return null
      const completionRaw = progress.completion_percentage
      if (completionRaw != null && Number.isFinite(Number(completionRaw))) {
        let pct = Number(completionRaw)
        if (pct >= 0 && pct <= 1) pct = pct * 100
        return Math.max(0, Math.min(100, pct))
      }
      if (progress.travel_distance_planned > 0) {
        let pct = (progress.travel_distance_actual / progress.travel_distance_planned) * 100
        return Math.max(0, Math.min(100, pct))
      }
      if (progress.cleaned_area_planned > 0) {
        let pct = (progress.cleaned_area_actual / progress.cleaned_area_planned) * 100
        return Math.max(0, Math.min(100, pct))
      }
      return null
    }

    const getWaterLiters = (progress: any) => {
      if (!progress) return null
      if (progress.water_consumption != null && progress.water_consumption > 0) {
        const val = Number(progress.water_consumption)
        const unit = progress.water_consumption_unit || 'percent'
        if (unit === 'liters' && val <= 1000) return val
        if (unit === 'percent' && val <= 100) return (val / 100) * 75
        return null
      }
      const begin = progress.water_level_begin || 0
      const end = progress.water_level_end || 0
      if (begin > 100 || end > 100 || end > begin) return null
      return ((begin - end) / 100) * 75
    }

    const getEleckWh = (progress: any) => {
      if (!progress) return null
      const begin = progress.battery_begin || 0
      const end = progress.battery_end || 0
      if (begin > 100 || end > 100 || end > begin) return null
      const capacityKWh = (progress.battery_capacity_wh ?? 11000) / 1000
      return ((begin - end) / 100) * capacityKWh
    }

    filteredData.events.forEach(e => {
      const date = format(parseISO(e.created_at), 'yyyy-MM-dd')
      const area = e.mission_progress?.cleaned_area_actual || 0
      if (area <= 0) return

      const areaPlanned = e.mission_progress?.cleaned_area_planned || 0
      const time = e.mission_progress?.duration_actual || 0
      const overallTime = e.mission_progress?.duration_overall || e.mission_progress?.duration_actual || 0
      const prod = e.mission_progress?.cleaning_performance || 0

      if (!aggregatedData[date]) {
        aggregatedData[date] = {
          area: 0,
          sumAreaPlanned: 0,
          time: 0,
          overallTime: 0,
          sumProductivity: 0,
          prodCount: 0,
          sumCompletion: 0,
          completionCount: 0,
          eventsCount: 0,
          completedCount: 0,
          canceledCount: 0,
          sumWaterL: 0,
          sumAreaForWater: 0,
          sumEleckWh: 0,
          sumAreaForElec: 0,
        }
      }
      aggregatedData[date].area += area
      aggregatedData[date].sumAreaPlanned += areaPlanned
      aggregatedData[date].time += time
      aggregatedData[date].overallTime += overallTime
      aggregatedData[date].eventsCount += 1

      // Consumption calculation
      const waterL = getWaterLiters(e.mission_progress)
      const eleckWh = getEleckWh(e.mission_progress)
      if (waterL !== null && eleckWh !== null) {
        aggregatedData[date].sumWaterL += waterL
        aggregatedData[date].sumAreaForWater += area
        aggregatedData[date].sumEleckWh += eleckWh
        aggregatedData[date].sumAreaForElec += area
      }

      const status = getStatus(e)
      if (status === 4) aggregatedData[date].completedCount += 1
      else if (status === 7 || status === 8) aggregatedData[date].canceledCount += 1

      if (prod > 0) {
        aggregatedData[date].sumProductivity += prod
        aggregatedData[date].prodCount += 1
      }
      const comp = calcCompletion(e.mission_progress)
      if (comp !== null) {
        aggregatedData[date].sumCompletion += comp
        aggregatedData[date].completionCount += 1
      }
    })

    filteredData.odysseys.forEach(o => {
      const date = format(parseISO(o.created_at), 'yyyy-MM-dd')
      const area = o.overall_progress?.cleaned_area_actual || 0
      if (area <= 0) return

      const areaPlanned = o.overall_progress?.cleaned_area_planned || 0
      const time = o.overall_progress?.duration_actual || 0
      const overallTime = o.overall_progress?.duration_overall || o.overall_progress?.duration_actual || 0
      const prod = o.overall_progress?.cleaning_performance || 0

      if (!aggregatedData[date]) {
        aggregatedData[date] = {
          area: 0,
          sumAreaPlanned: 0,
          time: 0,
          overallTime: 0,
          sumProductivity: 0,
          prodCount: 0,
          sumCompletion: 0,
          completionCount: 0,
          eventsCount: 0,
          completedCount: 0,
          canceledCount: 0,
          sumWaterL: 0,
          sumAreaForWater: 0,
          sumEleckWh: 0,
          sumAreaForElec: 0,
        }
      }
      aggregatedData[date].area += area
      aggregatedData[date].sumAreaPlanned += areaPlanned
      aggregatedData[date].time += time
      aggregatedData[date].overallTime += overallTime
      aggregatedData[date].eventsCount += 1

      // Consumption calculation
      const waterL = getWaterLiters(o.overall_progress)
      const eleckWh = getEleckWh(o.overall_progress)
      if (waterL !== null && eleckWh !== null) {
        aggregatedData[date].sumWaterL += waterL
        aggregatedData[date].sumAreaForWater += area
        aggregatedData[date].sumEleckWh += eleckWh
        aggregatedData[date].sumAreaForElec += area
      }

      const status = getStatus(o)
      if (status === 4) aggregatedData[date].completedCount += 1
      else if (status === 7 || status === 8) aggregatedData[date].canceledCount += 1
      if (prod > 0) {
        aggregatedData[date].sumProductivity += prod
        aggregatedData[date].prodCount += 1
      }
      const comp = calcCompletion(o.overall_progress)
      if (comp !== null) {
        aggregatedData[date].sumCompletion += comp
        aggregatedData[date].completionCount += 1
      }
    })

    // Generate all dates in range
    let allDates: Date[] = []
    try {
      allDates = eachDayOfInterval({
        start: effectiveDateRange.startDate,
        end: effectiveDateRange.endDate,
      })
    } catch (e) {
      const today = new Date()
      allDates = [today]
    }

    return allDates.map(d => {
      const dateKey = format(d, 'yyyy-MM-dd')
      const data = aggregatedData[dateKey] || {
        area: 0,
        sumAreaPlanned: 0,
        time: 0,
        overallTime: 0,
        sumProductivity: 0,
        prodCount: 0,
        sumCompletion: 0,
        completionCount: 0,
        eventsCount: 0,
        sumWaterL: 0,
        sumAreaForWater: 0,
        sumEleckWh: 0,
        sumAreaForElec: 0,
      }
      return {
        fullDate: dateKey,
        date: format(d, 'MMM dd'),
        area: Math.round(data.area * 100) / 100,
        areaPlanned: Math.round(data.sumAreaPlanned * 100) / 100,
        time: Math.round((data.time / 3600) * 100) / 100,
        overallTime: Math.round((data.overallTime / 3600) * 100) / 100,
        productivity: data.prodCount > 0 ? Math.round((data.sumProductivity / data.prodCount) * 100) / 100 : 0,
        completion: data.completionCount > 0 ? Math.round((data.sumCompletion / data.completionCount) * 100) / 100 : 0,
        waterPerM2: data.sumAreaForWater > 0 ? Math.round((data.sumWaterL / data.sumAreaForWater) * 1000) / 1000 : 0,
        elecWhPerM2:
          data.sumAreaForElec > 0 ? Math.round(((data.sumEleckWh * 1000) / data.sumAreaForElec) * 1000) / 1000 : 0,
        eventsCount: data.eventsCount,
        completedCount: data.completedCount,
        canceledCount: data.canceledCount,
      }
    })
  }, [filteredData, effectiveDateRange])

  const avgActualArea = useMemo(() => {
    const days = chartData.filter(d => d.area > 0)
    return days.length > 0 ? days.reduce((sum, d) => sum + d.area, 0) / days.length : 0
  }, [chartData])

  // 5. Derived Filter Options (Dynamic)
  const filterOptions = useMemo(() => {
    const allStats = [...rawEvents, ...rawOdysseys]
    const getVendor = (item: any): VendorCode => (item.vendor === VENDOR.GAUSIUM ? VENDOR.GAUSIUM : VENDOR.AUTONOMICS)

    // Maps to check reachability (missions/tasks: Autonomics = numeric id, Gausium = "map:xxx" / "task:xxx")
    const reachableRobotSns = new Set<string>()
    if (filterRobotSn?.trim()) reachableRobotSns.add(filterRobotSn.trim())
    const reachableMissions = new Map<
      number | string,
      { id: number | string; name: string; kind: 'scenario' | 'mission' | 'map' }
    >()
    const reachableTaskIds = new Set<number | string>()
    const taskIdToName = new Map<number | string, string>()

    allStats.forEach((item: any) => {
      const missionIds: (number | string)[] = []
      if (item.scenario_id) {
        missionIds.push(item.scenario_id)
        if (!reachableMissions.has(item.scenario_id)) {
          reachableMissions.set(item.scenario_id, {
            id: item.scenario_id,
            name: missionScenarioCatalogNames.scenarios[item.scenario_id] ?? `Scenario ${item.scenario_id}`,
            kind: 'scenario',
          })
        }
      }
      if (item.mission_id) missionIds.push(item.mission_id)
      if (item.map) {
        const mapId = `map:${item.map}`
        missionIds.push(mapId)
        reachableMissions.set(mapId, { id: mapId, name: item.map, kind: 'map' })
      }

      if (item.missions_info) {
        item.missions_info.forEach((m: any) => {
          missionIds.push(m.id)
          reachableMissions.set(m.id, { id: m.id, name: m.name, kind: 'mission' })
        })
      }
      for (const mission of item.missions_progress ?? []) {
        const id = coerceDashboardId(mission?.id)
        if (id == null || id === item.odyssey_id || id === coerceDashboardId(item.vendor_origin_record_id)) continue
        missionIds.push(id)
        if (!reachableMissions.has(id)) {
          const name = String(mission?.name ?? '').trim()
          reachableMissions.set(id, {
            id,
            name: name || missionScenarioCatalogNames.missions[id] || `Mission ${id}`,
            kind: 'mission',
          })
        }
      }

      if (item.mission_id && !reachableMissions.has(item.mission_id)) {
        reachableMissions.set(item.mission_id, {
          id: item.mission_id,
          name: missionScenarioCatalogNames.missions[item.mission_id] ?? `Mission ${item.mission_id}`,
          kind: 'mission',
        })
      }

      const effectiveTaskId: number | string =
        item.vendor === VENDOR.GAUSIUM
          ? item.mission_progress?.name && item.map
            ? `task:${item.mission_progress.name}`
            : null
          : item.task_id ?? item.odyssey_id ?? null
      if (effectiveTaskId != null) {
        const taskName =
          typeof effectiveTaskId === 'string' && effectiveTaskId.startsWith('task:')
            ? effectiveTaskId.slice(5)
            : `Task ${effectiveTaskId}`
        taskIdToName.set(effectiveTaskId, taskName)
      }

      const matchesVendor = !filterVendor || getVendor(item) === filterVendor
      const matchesFacility = !filterFacilityId || facilityRobotSns.has(String(item.robot_sn).trim())
      const matchesRobot = !filterRobotSn || String(item.robot_sn).trim() === String(filterRobotSn).trim()
      const matchesMission =
        !filterMissionId || missionIds.includes(filterMissionId) || missionIds.includes(Number(filterMissionId))
      const matchesTask =
        !filterTaskId ||
        effectiveTaskId === filterTaskId ||
        (filterTaskId && typeof effectiveTaskId === 'number' && effectiveTaskId === Number(filterTaskId))

      if (matchesVendor && matchesFacility && matchesRobot && matchesMission && effectiveTaskId != null) {
        reachableTaskIds.add(effectiveTaskId)
      }
      if (matchesVendor && matchesFacility && matchesMission && (!filterTaskId || matchesTask)) {
        reachableRobotSns.add(String(item.robot_sn).trim())
      }
    })

    const finalReachableMissionIds = new Set<number | string>()
    allStats.forEach((item: any) => {
      const effectiveTaskId =
        item.vendor === VENDOR.GAUSIUM
          ? item.mission_progress?.name && item.map
            ? `task:${item.mission_progress.name}`
            : null
          : item.task_id ?? item.odyssey_id ?? null
      const matchesFacility = !filterFacilityId || facilityRobotSns.has(String(item.robot_sn).trim())
      const matchesRobot = !filterRobotSn || String(item.robot_sn).trim() === String(filterRobotSn).trim()
      const matchesTask =
        !filterTaskId ||
        effectiveTaskId === filterTaskId ||
        (filterTaskId && typeof effectiveTaskId === 'number' && effectiveTaskId === Number(filterTaskId))
      const matchesVendor = !filterVendor || getVendor(item) === filterVendor

      if (matchesVendor && matchesFacility && matchesRobot && matchesTask) {
        if (item.scenario_id) finalReachableMissionIds.add(item.scenario_id)
        if (item.mission_id) finalReachableMissionIds.add(item.mission_id)
        if (item.map) finalReachableMissionIds.add(`map:${item.map}`)
        if (item.missions_info) item.missions_info.forEach((m: any) => finalReachableMissionIds.add(m.id))
        reportedMissionIds(item).forEach(id => finalReachableMissionIds.add(id))
      }
    })

    // Only robots that have events matching current mission/task filters (same logic as Autonomics; do not add all GS robots when filter is set)
    const robotsList = Array.from(reachableRobotSns)
      .sort()
      .map(sn => ({
        id: sn,
        serialNumber: sn,
        name: sn,
      }))

    const missionsList = Array.from(finalReachableMissionIds)
      .map(id => {
        const raw = reachableMissions.get(id) || {
          id,
          name: typeof id === 'string' ? String(id).replace(/^map:/, '') : `Mission ${id}`,
          kind: (typeof id === 'string' && String(id).startsWith('map:') ? 'map' : 'mission') as
            | 'scenario'
            | 'mission'
            | 'map',
        }
        if (typeof raw.id === 'number') {
          return { ...raw, displayName: `${raw.id} - ${raw.name}` }
        }
        return { ...raw, displayName: raw.name }
      })
      .sort((a, b) => {
        const order = { scenario: 0, mission: 1, map: 2 }
        const byKind = order[a.kind] - order[b.kind]
        if (byKind !== 0) return byKind
        return String(a.displayName).localeCompare(String(b.displayName))
      })

    const tasksList = Array.from(reachableTaskIds)
      .map(id => ({
        id,
        name:
          typeof id === 'number'
            ? `${id} - ${taskIdToName.get(id) || `Task ${id}`}`
            : taskIdToName.get(id) || String(id).replace(/^task:/, ''),
      }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)))

    if (filterRobotSn && !robotsList.some(r => String(r.serialNumber).trim() === filterRobotSn.trim())) {
      robotsList.unshift({ id: filterRobotSn, serialNumber: filterRobotSn, name: filterRobotSn })
    }
    if (filterMissionId && !missionsList.some(m => String(m.id) === filterMissionId)) {
      const isMap = filterMissionId.startsWith('map:')
      const id = isMap ? filterMissionId : Number(filterMissionId)
      const name = isMap ? filterMissionId.slice(4) : `Mission ${filterMissionId}`
      const kind = (isMap ? 'map' : 'mission') as 'scenario' | 'mission' | 'map'
      const displayName = typeof id === 'number' ? `${id} - ${name}` : name
      missionsList.unshift({ id, name, kind, displayName })
    }
    if (filterTaskId && !tasksList.some(t => String(t.id) === filterTaskId)) {
      const name = filterTaskId.startsWith('task:') ? filterTaskId.slice(5) : `Task ${filterTaskId}`
      tasksList.unshift({ id: filterTaskId.startsWith('task:') ? filterTaskId : Number(filterTaskId), name })
    }

    return {
      robots: robotsList,
      missions: missionsList,
      tasks: tasksList,
    }
  }, [
    rawEvents,
    rawOdysseys,
    gausiumRobotSns,
    filterRobotSn,
    filterMissionId,
    filterTaskId,
    filterFacilityId,
    filterVendor,
    facilityRobotSns,
    missionScenarioCatalogNames,
  ])

  const reachableVendors = useMemo(() => {
    const allStats = [...rawEvents, ...rawOdysseys]
    const getVendor = (item: any): VendorCode => (item.vendor === VENDOR.GAUSIUM ? VENDOR.GAUSIUM : VENDOR.AUTONOMICS)
    const vendors = new Set<VendorCode>()

    allStats.forEach((item: any) => {
      const sn = String(item.robot_sn ?? '').trim()
      if (!sn) return

      // Respect already applied filters except vendor itself.
      if (filterFacilityId && !facilityRobotSns.has(sn)) return
      if (filterRobotSn && String(filterRobotSn).trim() && sn !== String(filterRobotSn).trim()) return

      // Mission filter (scenario/mission/map)
      if (filterMissionId) {
        if (filterMissionId.startsWith('map:')) {
          const mapId = filterMissionId.slice(4)
          if (item.vendor !== VENDOR.GAUSIUM || item.map !== mapId) return
        } else {
          const num = Number(filterMissionId)
          if (!Number.isFinite(num)) return
          const inMissionsInfo = (item.missions_info || []).some((m: any) => m.id === num)
          const inReported = reportedMissionIds(item).includes(num)
          if (!(item.mission_id === num || item.scenario_id === num || inMissionsInfo || inReported)) return
        }
      }

      // Task filter
      if (filterTaskId) {
        if (filterTaskId.startsWith('task:')) {
          const name = filterTaskId.slice(5)
          if (item.vendor !== VENDOR.GAUSIUM || item.mission_progress?.name !== name) return
        } else {
          const num = Number(filterTaskId)
          if (!Number.isFinite(num)) return
          if (!(item.task_id === num || item.odyssey_id === num)) return
        }
      }

      vendors.add(getVendor(item))
    })

    return Array.from(vendors)
  }, [rawEvents, rawOdysseys, filterFacilityId, facilityRobotSns, filterRobotSn, filterMissionId, filterTaskId])

  const filteredFacilities = useMemo(() => {
    if (!facilities?.length) return []

    // If we don't have mapping yet, do not over-filter.
    if (!facilityRobotSnsById || Object.keys(facilityRobotSnsById).length === 0) return facilities

    // If no other filters applied, keep all.
    const hasOtherFilters =
      Boolean(filterRobotSn?.trim()) || Boolean(filterMissionId) || Boolean(filterTaskId) || Boolean(filterVendor)

    const allStats = [...rawEvents, ...rawOdysseys]
    const getVendor = (item: any): VendorCode => (item.vendor === VENDOR.GAUSIUM ? VENDOR.GAUSIUM : VENDOR.AUTONOMICS)

    const reachableRobotSns = new Set<string>()
    allStats.forEach((item: any) => {
      const sn = String(item.robot_sn ?? '').trim()
      if (!sn) return
      if (filterVendor && getVendor(item) !== filterVendor) return
      if (filterRobotSn?.trim() && sn !== filterRobotSn.trim()) return

      // Mission filter
      if (filterMissionId) {
        if (filterMissionId.startsWith('map:')) {
          const mapId = filterMissionId.slice(4)
          if (item.vendor !== VENDOR.GAUSIUM || item.map !== mapId) return
        } else {
          const num = Number(filterMissionId)
          if (!Number.isFinite(num)) return
          const inMissionsInfo = (item.missions_info || []).some((m: any) => m.id === num)
          const inReported = reportedMissionIds(item).includes(num)
          if (!(item.mission_id === num || item.scenario_id === num || inMissionsInfo || inReported)) return
        }
      }

      // Task filter
      if (filterTaskId) {
        if (filterTaskId.startsWith('task:')) {
          const name = filterTaskId.slice(5)
          if (item.vendor !== VENDOR.GAUSIUM || item.mission_progress?.name !== name) return
        } else {
          const num = Number(filterTaskId)
          if (!Number.isFinite(num)) return
          if (!(item.task_id === num || item.odyssey_id === num)) return
        }
      }

      reachableRobotSns.add(sn)
    })

    // Add synthetic "no facility" option when some reachable robots are not in any facility.
    let hasReachableWithoutFacility = false
    if (facilityRobotSnsByIdReady) {
      const allInAnyFacility = new Set<string>()
      ;(Object.values(facilityRobotSnsById) as Set<string>[]).forEach((sns: Set<string>) => {
        sns.forEach((sn: string) => allInAnyFacility.add(sn))
      })
      hasReachableWithoutFacility = Array.from(reachableRobotSns).some(sn => !allInAnyFacility.has(sn))
    }

    const next = hasOtherFilters
      ? facilities.filter((f: any) => {
          const sns = facilityRobotSnsById[String(f.id)] || new Set<string>()
          return Array.from(reachableRobotSns).some(sn => sns.has(sn))
        })
      : facilities.slice()

    if (hasReachableWithoutFacility || filterFacilityId === FACILITY_NONE_ID) {
      next.unshift({ id: FACILITY_NONE_ID, name: FACILITY_NONE_LABEL } as any)
    }

    // Keep current selection visible even if mapping is incomplete.
    if (filterFacilityId && !next.some((f: any) => String(f.id) === String(filterFacilityId))) {
      const current = facilities.find((f: any) => String(f.id) === String(filterFacilityId))
      if (current) next.unshift(current)
    }
    return next
  }, [
    facilities,
    facilityRobotSnsById,
    facilityRobotSnsByIdReady,
    rawEvents,
    rawOdysseys,
    filterRobotSn,
    filterMissionId,
    filterTaskId,
    filterVendor,
    filterFacilityId,
  ])

  /** Context for the AI agent to understand current filter selections and date range. */
  const chatflowConfig = useMemo(() => {
    const facilityName = facilities.find(f => String(f.id) === String(filterFacilityId))?.name || 'All Facilities'
    const robotName = filterRobotSn ? robotNamesBySn[filterRobotSn] || filterRobotSn : 'All Robots'
    const startDate = format(effectiveDateRange.startDate, 'yyyy-MM-dd')
    const endDate = format(effectiveDateRange.endDate, 'yyyy-MM-dd')
    const dateSummary = dateRangeType === 'week' ? 'Last Week' : dateRangeType === 'day' ? 'Today' : 'Last 30 Days'

    const contextSummary = `
Current Dashboard Context:
- Period: ${startDate} to ${endDate}
- Robot: ${robotName} (SN: ${filterRobotSn || 'All'})
- Facility: ${facilityName} (ID: ${filterFacilityId || 'All'})
- Vendor: ${filterVendor || 'All'}
- Mission ID: ${filterMissionId || 'All'}
- Task ID: ${filterTaskId || 'All'}
`.trim()

    return {
      dateSummary,
      vars: {
        startDate,
        endDate,
        robotSn: filterRobotSn || 'all',
        robotName,
        facilityId: filterFacilityId || 'all',
        facilityName,
        vendor: filterVendor || 'all',
        missionId: filterMissionId || 'all',
        taskId: filterTaskId || 'all',
      },
      overrideConfig: {
        systemMessagePrompt: `You are an AI assistant for the FMS Dashboard. The current dashboard filters are MANDATORY and fixed to: ${contextSummary}. Analysis MUST be strictly limited to the period ${startDate} to ${endDate}.`,
      },
    }
  }, [effectiveDateRange, dateRangeType, filterMissionId, filterTaskId])

  /** Dynamic starter prompt that includes current filter context as a direct message. */
  const starterPrompt = useMemo(() => {
    const robotContext = filterRobotSn ? `Robot ${filterRobotSn}` : 'all robots'
    const dateContext = `from ${format(effectiveDateRange.startDate, 'dd/MM/yyyy')} to ${format(
      effectiveDateRange.endDate,
      'dd/MM/yyyy'
    )}`
    const facilityName = facilities.find(f => String(f.id) === String(filterFacilityId))?.name || ''
    const secondaryContext = [
      facilityName ? `in ${facilityName}` : '',
      filterVendor ? `vendor ${filterVendor === VENDOR.AUTONOMICS ? 'Autonomics' : 'Gausium'}` : '',
      filterMissionId ? `mission ${filterMissionId}` : '',
      filterTaskId ? `task ${filterTaskId}` : '',
    ]
      .filter(Boolean)
      .join(', ')

    return `Analyze cleaning operations for ${robotContext} ${dateContext}${
      secondaryContext ? ` (${secondaryContext})` : ''
    }`
  }, [filterRobotSn, effectiveDateRange, filterFacilityId, facilities, filterVendor, filterMissionId, filterTaskId])

  // Fetch statistics only for the requested filters without automatically resetting them
  // This avoids double fetches and preserves selections even if no data is present in the timeframe.

  const handleClearFilters = () => {
    setUrl({
      robot_sn: '',
      missionId: '',
      taskId: '',
      facility: '',
      vendor: '',
      rfav: false,
    })
  }

  const formatTime = (seconds: number) => {
    if (!seconds) return '0h 0m'
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    return `${h}h ${m}m`
  }

  const formatHoursToTime = (hoursTotal: number) => {
    const h = Math.floor(hoursTotal)
    const m = Math.floor((hoursTotal - h) * 60)
    return `${h}h ${m}m`
  }

  const handleExport = () => {
    const combinedStats = [...filteredData.events, ...filteredData.odysseys]
    const events: ProcessedEvent[] = []
    addEventsStatistics(events, combinedStats, [], new Map(), new Map())
    exportScheduleToCSV(events)
  }

  const scenarioMissionOptions = useMemo(
    () => filterOptions.missions.filter((m: any) => m.kind === 'scenario'),
    [filterOptions.missions]
  )
  const missionOptions = useMemo(
    () => filterOptions.missions.filter((m: any) => m.kind === 'mission'),
    [filterOptions.missions]
  )
  const mapOptions = useMemo(
    () => filterOptions.missions.filter((m: any) => m.kind === 'map'),
    [filterOptions.missions]
  )

  const noFavoriteRobots = favoritesOnly && robotsCatalogLoaded && favoriteRobotIds.length === 0

  const chartHeight = isMobile ? 330 : 430
  const chartMargin = { top: 10, right: isMobile ? 28 : 40, left: isMobile ? 6 : 40, bottom: 30 }

  return (
    <div className={style.container}>
      {isGausiumSyncing && (
        <div className={nonBlockingSyncLoaderStyle.nonBlockingSyncLoader} title="Gausium background sync in progress">
          <Loader />
        </div>
      )}
      <div className={style.header}>
        <Title text={t('header.analytics')} />
        <CustomButton.Outlined onClick={handleExport} style={{ width: isMobile ? '100px' : '150px' }}>
          Export
        </CustomButton.Outlined>
      </div>

      <div className={style.content}>
        <div className={style.summaryCards}>
          <div className={style.summaryCard}>
            <span className={style.summaryLabel}>Total Area</span>
            <span className={style.summaryValue}>{Math.round(stats.totalArea).toLocaleString()} m²</span>
          </div>
          <div className={style.summaryCard}>
            <span className={style.summaryLabel}>Total Working Time</span>
            <span className={style.summaryValue}>{formatTime(stats.totalTime)}</span>
          </div>
          <div className={style.summaryCard}>
            <span className={style.summaryLabel}>Overall Time</span>
            <span className={style.summaryValue}>{formatTime(stats.totalOverallTime)}</span>
          </div>
          <div className={style.summaryCard}>
            <span className={style.summaryLabel}>Avg Productivity</span>
            <span className={style.summaryValue}>{Math.round(stats.avgPerformance)} m²/h</span>
          </div>
          <div className={style.summaryCard}>
            <span className={style.summaryLabel}>Avg Completion Rate</span>
            <span className={style.summaryValue}>{Math.round(stats.avgCompletion)}%</span>
          </div>
          <div className={style.summaryCard}>
            <span className={style.summaryLabel}>Total Water</span>
            <span className={style.summaryValue}>{Math.round(stats.totalWaterL).toLocaleString()} L</span>
          </div>
          <div className={style.summaryCard}>
            <span className={style.summaryLabel}>Total Energy</span>
            <span className={style.summaryValue}>{stats.totalEleckWh.toFixed(2)} kWh</span>
          </div>

          {isMobile && (
            <Button.Outlined
              className={clsx(style.mobileFilterToggle, style.summaryFilterToggle, {
                [style.filterToggleActive]: hasActiveFilters,
              })}
              onClick={() => setShowFilters(!showFilters)}
            >
              {'Filters'} {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Button.Outlined>
          )}
        </div>

        <div className={clsx(style.filtersBlock, isMobile && !showFilters && style.filtersBlockHidden)}>
          <div className={style.leftFilters}>
            <Dropdown
              className={style.filterDate}
              minWidth={150}
              currentOption={
                dateRangeType === 'day'
                  ? { value: 'day', text: 'Today' }
                  : dateRangeType === 'week'
                  ? { value: 'week', text: 'Last 7 Days' }
                  : dateRangeType === 'month'
                  ? { value: 'month', text: 'Last 30 Days' }
                  : { value: 'custom', text: 'Custom Range' }
              }
              options={[
                { value: 'month', text: 'Last 30 Days' },
                { value: 'week', text: 'Last 7 Days' },
                { value: 'day', text: 'Today' },
                { value: 'custom', text: 'Custom Range' },
              ]}
              onClickOption={(opt: IOption) => {
                // Presets must not chain setCustomStart/End: those force range=custom.
                setDateRangeType(opt.value as 'day' | 'week' | 'month' | 'custom')
              }}
            />

            <div className={style.dateInputs}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={parseISO(customStartDate)}
                  onChange={newDate => {
                    if (newDate) {
                      setCustomStartDate(format(newDate, 'yyyy-MM-dd'))
                    }
                  }}
                  format="dd/MM/yyyy"
                  slots={{ textField: TextField }}
                  slotProps={DATE_PICKER_SLOT_PROPS}
                />
                <span style={{ color: '#64748b' }}>-</span>
                <DatePicker
                  value={parseISO(customEndDate)}
                  onChange={newDate => {
                    if (newDate) {
                      setCustomEndDate(format(newDate, 'yyyy-MM-dd'))
                    }
                  }}
                  format="dd/MM/yyyy"
                  slots={{ textField: TextField }}
                  slotProps={DATE_PICKER_SLOT_PROPS}
                />
              </LocalizationProvider>
            </div>
          </div>

          <div className={style.rightFilters}>
            <div className={style.filters}>
              <Dropdown
                className={style.filterFacility}
                minWidth={300}
                canCopy={true}
                currentOption={
                  filterFacilityId
                    ? {
                        value: filterFacilityId,
                        text:
                          filterFacilityId === FACILITY_NONE_ID
                            ? FACILITY_NONE_LABEL
                            : filteredFacilities.find(f => String(f.id) === filterFacilityId)?.name || filterFacilityId,
                      }
                    : undefined
                }
                placeholder="All Facilities"
                options={[
                  { value: '', text: 'All Facilities' },
                  ...filteredFacilities.map(f => ({ value: String(f.id), text: f.name })),
                ]}
                onClickOption={(opt: IOption) => setFilterFacilityId(String(opt.value))}
              />

              <Dropdown
                className={style.filterVendor}
                minWidth={300}
                canCopy={true}
                currentOption={
                  filterVendor
                    ? { value: filterVendor, text: filterVendor === VENDOR.AUTONOMICS ? 'Autonomics' : 'Gausium' }
                    : undefined
                }
                placeholder="All Vendors"
                options={[
                  { value: '', text: 'All Vendors' },
                  ...reachableVendors.map(v => ({
                    value: v,
                    text: v === VENDOR.AUTONOMICS ? 'Autonomics' : 'Gausium',
                  })),
                ]}
                onClickOption={(opt: IOption) => setFilterVendor(opt.value as VendorCode | '')}
              />

              <Dropdown
                className={style.filterRobot}
                minWidth={400}
                canCopy={true}
                currentOption={
                  filterRobotSn
                    ? {
                        value: filterRobotSn,
                        text: robotNamesBySn[filterRobotSn]
                          ? `${filterRobotSn} - ${robotNamesBySn[filterRobotSn]}`
                          : filterRobotSn,
                      }
                    : favoritesOnly
                      ? {
                          value: FAVORITES_DROPDOWN_VALUE,
                          text: t('favorites.only'),
                          leadingIcon: 'star',
                        }
                      : undefined
                }
                placeholder="All Robots"
                options={[
                  { value: '', text: 'All Robots' },
                  {
                    value: FAVORITES_DROPDOWN_VALUE,
                    text: t('favorites.only'),
                    leadingIcon: 'star',
                  },
                  ...filterOptions.robots
                    .filter(r => !favoritesOnly || favoriteSnSet.has(String(r.serialNumber).trim()))
                    .map(r => ({
                      value: String(r.serialNumber),
                      text: robotNamesBySn[String(r.serialNumber).trim()]
                        ? `${r.serialNumber} - ${robotNamesBySn[String(r.serialNumber).trim()]}`
                        : r.name || r.serialNumber,
                    })),
                ]}
                onClickOption={(opt: IOption) => {
                  const value = String(opt.value ?? '')
                  if (value === FAVORITES_DROPDOWN_VALUE) {
                    setUrl({ robot_sn: '', rfav: true })
                    return
                  }
                  setUrl({ robot_sn: value, rfav: false })
                }}
              />

              <Dropdown
                className={style.filterMission}
                minWidth={400}
                canCopy={true}
                currentOption={
                  filterMissionId
                    ? {
                        value: filterMissionId,
                        text:
                          filterOptions.missions.find(m => String(m.id) === filterMissionId)?.displayName ||
                          filterMissionId,
                      }
                    : undefined
                }
                placeholder="All Missions"
                options={[
                  { value: '', text: 'All Missions' },
                  ...scenarioMissionOptions.map((m, idx) => ({
                    value: String(m.id),
                    text: m.displayName,
                    subtitle: idx === 0 ? t('dashboard.filters.scenarios_group') : undefined,
                  })),
                  ...missionOptions.map((m, idx) => ({
                    value: String(m.id),
                    text: m.displayName,
                    subtitle: idx === 0 ? t('dashboard.filters.missions_group') : undefined,
                  })),
                  ...mapOptions.map((m, idx) => ({
                    value: String(m.id),
                    text: m.displayName,
                    subtitle: idx === 0 ? t('dashboard.filters.maps_group') : undefined,
                  })),
                ]}
                onClickOption={(opt: IOption) => setFilterMissionId(String(opt.value))}
              />

              <Dropdown
                className={style.filterTask}
                minWidth={300}
                canCopy={true}
                currentOption={
                  filterTaskId
                    ? {
                        value: filterTaskId,
                        text: filterOptions.tasks.find(t => String(t.id) === filterTaskId)?.name || filterTaskId,
                      }
                    : undefined
                }
                placeholder="All Tasks"
                options={[
                  { value: '', text: 'All Tasks' },
                  ...filterOptions.tasks.map(t => ({ value: String(t.id), text: t.name })),
                ]}
                onClickOption={(opt: IOption) => setFilterTaskId(String(opt.value))}
              />
            </div>

            <div className={style.clearButtonWrapper}>
              <Button.Outlined className={style.clearButton} onClick={handleClearFilters}>
                Clear Filters
              </Button.Outlined>
            </div>
          </div>
        </div>

        {noFavoriteRobots ? (
          <div className={style.favoritesEmpty}>
            <div className={style.favoritesEmptyTitle}>{t('favorites.empty_robots')}</div>
            <div className={style.favoritesEmptyDescription}>{t('favorites.empty_analytics_description')}</div>
          </div>
        ) : (
        <>
        <div className={style.tabsWrapper}>
          <AdaptiveTabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              mb: 3,
              borderBottom: 'var(--glass-border)',
              '& .MuiTabs-indicator': {
                backgroundColor: '#4e78f6',
                height: '3px',
                borderRadius: '3px',
              },
            }}
          >
            <StyledTab label="Performance" />
            <StyledTab label="Productivity" />
            <StyledTab label="Completion Rate" />
            <StyledTab label="ESG" />
          </AdaptiveTabs>
        </div>

        <div className={style.statsGrid}>
          {activeTab === 0 && (
            <Box className={style.box}>
              {/* <h3 className={style.cardTitle}>Cleaning History</h3> */}
              <div className={style.chartContainer}>
                {loading ? (
                  <div className={style.loader}>Loading statistical data...</div>
                ) : (
                  <ResponsiveContainer width="100%" height={chartHeight}>
                    <ComposedChart data={chartData} margin={chartMargin}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis
                        xAxisId="actual"
                        dataKey="fullDate"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        dy={10}
                        tickFormatter={(value: string) => format(parseISO(value), 'MMM dd')}
                        minTickGap={30}
                      />
                      <XAxis xAxisId="planned" dataKey="fullDate" hide />
                      <YAxis
                        yAxisId="left"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        tickFormatter={(value: number) => `${value}`}
                        label={{
                          value: 'Area (m²)',
                          angle: -90,
                          position: 'insideLeft',
                          fill: '#64748b',
                          offset: isMobile ? 5 : 0,
                          dy: isMobile ? 40 : 25,
                        }}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        label={{
                          value: 'Time (h)',
                          angle: 90,
                          position: 'insideRight',
                          fill: '#64748b',
                          offset: isMobile ? 10 : 20,
                          dy: isMobile ? 40 : 25,
                        }}
                      />
                      <Tooltip
                        animationDuration={150}
                        cursor={{ fill: 'var(--chart-cursor-fill)' }}
                        content={<CustomTooltip />}
                      />
                      <Legend content={renderChartLegend} />
                      <Bar
                        xAxisId="planned"
                        yAxisId="left"
                        dataKey="areaPlanned"
                        name="Planned Area (m²)"
                        fill="#90c3fdd8"
                        fillOpacity={1}
                        radius={[4, 4, 0, 0]}
                        barSize={40}
                      />
                      <Bar
                        xAxisId="actual"
                        yAxisId="left"
                        dataKey="area"
                        name="Actual Area (m²)"
                        fill="#4e78f6"
                        radius={[4, 4, 0, 0]}
                        barSize={40}
                      >
                        {chartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill="#4e78f6"
                            fillOpacity={0.8 + (index / chartData.length) * 0.2}
                          />
                        ))}
                      </Bar>
                      <Line
                        xAxisId="actual"
                        yAxisId="right"
                        type="monotone"
                        dataKey="overallTime"
                        name="Overall Time (h)"
                        stroke="var(--chart-line-overall)"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                      <Line
                        xAxisId="actual"
                        yAxisId="right"
                        type="monotone"
                        dataKey="time"
                        name="Working Time (h)"
                        stroke="var(--chart-line-working)"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                      <Line
                        xAxisId="actual"
                        yAxisId="left"
                        dataKey="area"
                        name="Avg. Actual Area (m²)"
                        stroke="#2E92FE"
                        strokeDasharray="5 5"
                        strokeWidth={0}
                        legendType="line"
                        dot={false}
                        activeDot={false}
                        tooltipType="none"
                      />
                      <ReferenceLine
                        isFront
                        xAxisId="actual"
                        yAxisId="left"
                        y={avgActualArea}
                        stroke="#2E92FE"
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        label={{
                          value: `${Math.round(avgActualArea)}`,
                          position: 'left',
                          fill: '#2E92FE',
                          fontSize: 12,
                          fontWeight: 700,
                          offset: 5,
                        }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Box>
          )}

          {activeTab === 1 && (
            <Box className={style.box}>
              {/* <h3 className={style.cardTitle}>Productivity</h3> */}
              <div className={style.chartContainer}>
                {loading ? (
                  <div className={style.loader}>Loading statistical data...</div>
                ) : (
                  <ResponsiveContainer width="100%" height={chartHeight}>
                    <ComposedChart data={chartData} margin={chartMargin}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis
                        dataKey="fullDate"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        dy={10}
                        tickFormatter={(value: string) => format(parseISO(value), 'MMM dd')}
                        minTickGap={30}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        label={{
                          value: 'Productivity (m²/h)',
                          angle: -90,
                          position: 'insideLeft',
                          fill: '#64748b',
                          offset: isMobile ? 10 : 0,
                          dy: 60,
                        }}
                      />
                      <Tooltip
                        animationDuration={150}
                        cursor={{ fill: 'var(--chart-cursor-fill)' }}
                        contentStyle={{
                          backgroundColor: 'var(--solid-bg)',
                          borderRadius: 'var(--radius-sm)',
                          border: 'var(--glass-border)',
                          boxShadow: 'var(--glass-shadow)',
                          fontSize: '12px',
                        }}
                        labelStyle={{ color: 'var(--text-main)' }}
                        formatter={(value: any, name: any) => {
                          if (name === 'Avg Productivity (Period) (m²/h)') return [null, null]
                          return [value, name]
                        }}
                        labelFormatter={(label: any, payload: readonly any[]) => {
                          if (payload && payload.length > 0) {
                            const count = payload[0].payload.eventsCount
                            return count ? `${label} (${count} ${count === 1 ? 'task' : 'tasks'})` : label
                          }
                          return label
                        }}
                      />
                      <Legend content={renderChartLegend} />
                      <Bar
                        dataKey="productivity"
                        name="Avg Productivity (Day) (m²/h)"
                        fill="#4e78f6"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={50}
                      >
                        {chartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill="#4e78f6"
                            fillOpacity={0.8 + (index / chartData.length) * 0.2}
                          />
                        ))}
                      </Bar>
                      <Line
                        dataKey="productivity"
                        name="Avg Productivity (Period) (m²/h)"
                        stroke="var(--chart-line-overall)"
                        strokeDasharray="5 5"
                        strokeWidth={0}
                        legendType="line"
                        dot={false}
                        activeDot={false}
                      />
                      <ReferenceLine
                        isFront
                        y={stats.avgPerformance}
                        stroke="var(--chart-line-overall)"
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        label={{
                          value: `${Math.round(stats.avgPerformance)}`,
                          position: 'left',
                          fill: 'var(--chart-line-overall)',
                          fontSize: 12,
                          fontWeight: 700,
                          offset: 5,
                        }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Box>
          )}

          {activeTab === 2 && (
            <Box className={style.box}>
              <div className={style.chartContainer}>
                {loading ? (
                  <div className={style.loader}>Loading statistical data...</div>
                ) : (
                  <ResponsiveContainer width="100%" height={chartHeight}>
                    <ComposedChart data={chartData} margin={chartMargin}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis
                        dataKey="fullDate"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        dy={10}
                        tickFormatter={(value: string) => format(parseISO(value), 'MMM dd')}
                        minTickGap={30}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        domain={[0, 100]}
                        label={{
                          value: 'Completion (%)',
                          angle: -90,
                          position: 'insideLeft',
                          fill: '#64748b',
                          offset: isMobile ? 10 : 0,
                          dy: 50,
                        }}
                      />
                      <Tooltip
                        animationDuration={150}
                        cursor={{ fill: 'var(--chart-cursor-fill)' }}
                        contentStyle={{
                          backgroundColor: 'var(--solid-bg)',
                          borderRadius: 'var(--radius-sm)',
                          border: 'var(--glass-border)',
                          boxShadow: 'var(--glass-shadow)',
                          fontSize: '12px',
                        }}
                        labelStyle={{ color: 'var(--text-main)' }}
                        formatter={(value: any, name: any) => {
                          if (name === 'Avg Completion (Period) (%)') return [null, null]
                          return [`${value}%`, name]
                        }}
                        labelFormatter={(label: any, payload: readonly any[]) => {
                          if (payload && payload.length > 0) {
                            const count = payload[0].payload.eventsCount
                            return count ? `${label} (${count} ${count === 1 ? 'task' : 'tasks'})` : label
                          }
                          return label
                        }}
                      />
                      <Legend content={renderChartLegend} />
                      <Bar
                        dataKey="completion"
                        name="Avg Completion (Day) (%)"
                        fill="#4e78f6"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={50}
                      >
                        {chartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill="#4e78f6"
                            fillOpacity={0.8 + (index / chartData.length) * 0.2}
                          />
                        ))}
                      </Bar>
                      <Line
                        dataKey="completion"
                        name="Avg Completion (Period) (%)"
                        stroke="var(--chart-line-overall)"
                        strokeDasharray="5 5"
                        strokeWidth={0}
                        legendType="line"
                        dot={false}
                        activeDot={false}
                      />
                      <ReferenceLine
                        isFront
                        y={stats.avgCompletion}
                        stroke="var(--chart-line-overall)"
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        label={{
                          value: `${Math.round(stats.avgCompletion)}`,
                          position: 'left',
                          fill: 'var(--chart-line-overall)',
                          fontSize: 12,
                          fontWeight: 700,
                          offset: 5,
                        }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Box>
          )}
          {activeTab === 3 && (
            <Box className={style.box}>
              <div className={style.chartContainer}>
                {loading ? (
                  <div className={style.loader}>Loading statistical data...</div>
                ) : (
                  <ResponsiveContainer width="100%" height={chartHeight}>
                    <ComposedChart data={chartData} margin={chartMargin}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis
                        dataKey="fullDate"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        dy={10}
                        tickFormatter={(value: string) => format(parseISO(value), 'MMM dd')}
                        minTickGap={30}
                      />
                      <YAxis
                        yAxisId="left"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        label={{
                          value: 'Water (L/m²)',
                          angle: -90,
                          position: 'insideLeft',
                          fill: '#64748b',
                          offset: isMobile ? 10 : 0,
                          dy: 50,
                        }}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        label={{
                          value: 'Electricity (Wh/m²)',
                          angle: 90,
                          position: 'insideRight',
                          fill: '#64748b',
                          offset: 10,
                          dy: 70,
                        }}
                      />
                      <Tooltip
                        animationDuration={150}
                        cursor={{ fill: 'var(--chart-cursor-fill)' }}
                        contentStyle={{
                          backgroundColor: 'var(--solid-bg)',
                          borderRadius: 'var(--radius-sm)',
                          border: 'var(--glass-border)',
                          boxShadow: 'var(--glass-shadow)',
                          fontSize: '12px',
                        }}
                        labelStyle={{ color: 'var(--text-main)' }}
                        formatter={(value: any, name: any) => {
                          if (name === 'Water (L/m²)') return [`${value} L/m²`, name]
                          if (name === 'Electricity (Wh/m²)') return [`${value} Wh/m²`, name]
                          return [value, name]
                        }}
                        labelFormatter={(label: any, payload: readonly any[]) => {
                          if (payload && payload.length > 0) {
                            const count = payload[0].payload.eventsCount
                            return count ? `${label} (${count} ${count === 1 ? 'task' : 'tasks'})` : label
                          }
                          return label
                        }}
                      />
                      <Legend content={renderChartLegend} />
                      <Bar
                        yAxisId="left"
                        dataKey="waterPerM2"
                        name="Water (L/m²)"
                        fill="#4e78f6"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={50}
                      >
                        {chartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill="#4e78f6"
                            fillOpacity={0.8 + (index / chartData.length) * 0.2}
                          />
                        ))}
                      </Bar>
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="elecWhPerM2"
                        name="Electricity (Wh/m²)"
                        stroke="var(--chart-line-overall)"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                      <Line
                        yAxisId="left"
                        name="Avg Water (Period) (L/m²)"
                        stroke="#2E92FE"
                        strokeDasharray="5 5"
                        strokeWidth={0}
                        legendType="line"
                        dot={false}
                        activeDot={false}
                      />
                      <Line
                        yAxisId="right"
                        name="Avg Electricity (Period) (Wh/m²)"
                        stroke="var(--chart-line-overall)"
                        strokeDasharray="5 5"
                        strokeWidth={0}
                        legendType="line"
                        dot={false}
                        activeDot={false}
                      />
                      <ReferenceLine
                        isFront
                        yAxisId="left"
                        y={stats.avgWaterPerM2}
                        stroke="#2E92FE"
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        label={{
                          value: `${stats.avgWaterPerM2.toFixed(3)}`,
                          position: 'left',
                          fill: '#2E92FE',
                          fontSize: 10,
                          fontWeight: 700,
                          offset: 5,
                        }}
                      />
                      <ReferenceLine
                        isFront
                        yAxisId="right"
                        y={stats.avgElecWhPerM2}
                        stroke="var(--chart-line-overall)"
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        label={{
                          value: `${stats.avgElecWhPerM2.toFixed(2)}`,
                          position: 'right',
                          fill: 'var(--chart-line-overall)',
                          fontSize: 10,
                          fontWeight: 700,
                          offset: 5,
                        }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Box>
          )}
        </div>
        </>
        )}
      </div>
      {SHOW_AI_AGENT && (
        <BubbleChat
          key={`${currentTheme}-${dateRangeType}-${filterRobotSn}-${filterVendor}-${filterFacilityId}-${filterMissionId}`}
          chatflowid="c1d81097-a85b-4bc2-ac4b-0f4058c472ce"
          apiHost="https://cloud.flowiseai.com"
          chatflowConfig={chatflowConfig}
          theme={{
            customCSS: `
              .fixed.rounded-lg {
                border-radius: 40px !important;
                background: transparent !important;
                box-shadow: none !important;
              }
              .chatbot-container {
                border-radius: 40px !important;
                background: ${isDark ? 'rgba(34, 34, 26, 0.95)' : 'rgba(255, 255, 255, 0.85)'} !important;
                backdrop-filter: blur(20px) !important;
                -webkit-backdrop-filter: blur(20px) !important;
                border: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.3)'} !important;
                overflow: hidden !important;
                box-shadow: var(--glass-shadow) !important;
                color: ${isDark ? '#f5f5f7' : '#1d1d1f'} !important;
              }
              .chatbot-container > div:first-child {
                border-radius: 40px 40px 0 0 !important;
                background: ${isDark ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)'} !important;
                color: ${isDark ? '#f5f5f7' : '#1d1d1f'} !important;
              }
              .chatbot-container > div:first-child * {
                color: inherit !important;
              }
              .chatbot-input {
                border-radius: 20px !important;
                margin: 10px !important;
                border: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.3)'} !important;
                background: ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.5)'} !important;
                color: ${isDark ? '#f5f5f7' : '#1d1d1f'} !important;
              }
              .chat-window {
                border-radius: 40px !important;
                overflow: hidden !important;
              }
              /* Style the typing indicator container bubble */
              .chatbot-host-bubble {
                background-color: ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(243, 244, 246, 0.7)'} !important;
                border-radius: 20px !important;
                padding: 12px 16px !important;
                margin-bottom: 10px !important;
                box-shadow: none !important;
              }
              .bubble-typing {
                background-color: transparent !important;
                background: transparent !important;
              }
              .bubble1, .bubble2, .bubble3 {
                background-color: ${isDark ? '#f5f5f7' : '#303235'} !important;
                opacity: 1 !important;
              }
              /* Style the starter prompt (suggestion buttons) */
              .host-container .chatbot-host-bubble {
                background-color: ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.05)'} !important;
                color: ${isDark ? '#f5f5f7' : '#303235'} !important;
                border: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'} !important;
                cursor: pointer !important;
              }
              .host-container:hover .chatbot-host-bubble {
                background-color: ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'} !important;
              }
            `,
            button: {
              backgroundColor: 'transparent',
              right: 20,
              bottom: 20,
              size: 64,
              dragAndDrop: false,
              customIconSrc: autonomicsIcon,
              autoWindowOpen: {
                autoOpen: false,
                openDelay: 2,
                autoOpenOnMobile: false,
              },
            },
            tooltip: {
              showTooltip: false,
              tooltipMessage: 'Analyze your cleaning operations',
              tooltipBackgroundColor: 'black',
              tooltipTextColor: 'white',
              tooltipFontSize: 16,
            },
            // disclaimer: {
            //   title: 'Disclaimer',
            //   message:
            //     'By using this chatbot, you agree to the <a target="_blank" href="https://flowiseai.com/terms">Terms & Condition</a>',
            //   textColor: isDark ? '#f5f5f7' : '#1d1d1f',
            //   buttonColor: '#3b82f6',
            //   buttonText: 'Start Chatting',
            //   buttonTextColor: 'white',
            //   backgroundColor: isDark ? 'rgba(34, 34, 26, 0.95)' : 'rgba(255, 255, 255, 0.9)',
            //   blurredBackgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)',
            // },
            chatWindow: {
              showTitle: true,
              showAgentMessages: true,
              title: 'Autonomics AI Assistant',
              titleAvatarSrc: autonomicsIcon,
              welcomeMessage: `Hello! I am your AI assistant. I am ready to analyze your cleaning operations. How can I help?`,
              errorMessage: 'Something went wrong',
              backgroundColor: 'transparent',
              height: 700,
              width: 1400,
              fontSize: 15,
              starterPrompts: [starterPrompt],
              starterPromptFontSize: 14,
              clearChatOnReload: true,
              sourceDocsTitle: 'Sources:',
              renderHTML: true,
              botMessage: {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(243, 244, 246, 0.7)',
                textColor: isDark ? '#f5f5f7' : '#1d1d1f',
                showAvatar: true,
                avatarSrc: autonomicsIcon,
              },
              userMessage: {
                backgroundColor: '#3B81F6',
                textColor: '#ffffff',
                showAvatar: true,
                avatarSrc:
                  'https://raw.githubusercontent.com/zahidkhawaja/langchain-chat-nextjs/main/public/usericon.png',
              },
              textInput: {
                placeholder: 'Type your question',
                backgroundColor: 'transparent',
                textColor: isDark ? '#f5f5f7' : '#1d1d1f',
                sendButtonColor: '#3B81F6',
                maxChars: 1000,
                maxCharsWarningMessage: 'You exceeded the characters limit. Please input less than 1000 characters.',
                autoFocus: true,
                sendMessageSound: true,
                sendSoundLocation: 'send_message.mp3',
                receiveMessageSound: true,
                receiveSoundLocation: 'receive_message.mp3',
              },
              feedback: {
                color: isDark ? '#f5f5f7' : '#303235',
              },
              dateTimeToggle: {
                date: true,
                time: true,
              },
              footer: {
                textColor: isDark ? 'rgba(245, 245, 247, 0.6)' : '#303235',
                text: '',
                company: '',
                companyLink: 'https://flowiseai.com',
              },
            },
          }}
        />
      )}
    </div>
  )
}

export default Dashboard
